import { Box, Collapse, List, ListItem, Tooltip, Typography, useTheme } from '@mui/material';
import {
  Children,
  CSSProperties,
  FC,
  isValidElement,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { SidebarStateContext } from '../../../providers/MainProviders';
import styled from 'styled-components';
import { matchPath, NavLink } from 'react-router-dom';
import { customColors } from '../../../constants/theme';

interface Props {
  text: string;
  icon?: JSX.Element;
  children?: ReactNode;
  to: string;
  tooltip?: string;
  style?: CSSProperties;
}

const CustomNavLink = styled(NavLink)<{ $expanded?: boolean }>`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.palette.text.primary};
  text-decoration: none;
  padding: ${(props) => (props.$expanded ? props.theme.spacing(2) : props.theme.spacing(1))};

  &.active {
    background-color: rgba(${(props) => customColors.primary.main}, 0.1);
    color: ${(props) =>
      props.theme.palette.mode == 'light'
        ? props.theme.palette.primary.main
        : props.theme.palette.text.primary};
    border-radius: ${(props) => (props.$expanded ? '0' : '50%')};
  }
`;

const CustomNavLinkIcon = styled.div<{ $expanded?: boolean }>`
  display: flex;
  align-items: center;
  margin-right: ${(props) => (props.$expanded ? props.theme.spacing(1) : 0)};
`;

const CustomListItem = styled.div`
  display: flex;
  padding: ${(props) => props.theme.spacing(2)} ${(props) => props.theme.spacing(2)};

  &:hover {
    cursor: pointer;
  }
`;

const CustomListItemIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${(props) => props.theme.spacing(1)};
`;

const CustomListItemCollapsibleIcon = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const SidebarNavItem: FC<Props> = ({ text, icon, children, to, tooltip, style }) => {
  const [open, setOpen] = useState(false);
  const { expanded } = useContext(SidebarStateContext);

  const theme = useTheme();

  const handleClick = () => {
    setOpen(!open);
  };

  const hasChildren = Children.count(children) > 0;

  // Check if any child route matches current location
  const hasActiveChild =
    hasChildren &&
    Children.toArray(children).some((child) => {
      if (isValidElement(child)) {
        const childTo = child.props.to as string | undefined;
        if (childTo) {
          return matchPath({ path: childTo, end: false }, window.location.pathname);
        }
      }
      return false;
    });

  // Auto-expand if a child is active
  useEffect(() => {
    if (hasActiveChild) {
      setOpen(true);
    }
  }, [hasActiveChild]);

  return (
    <>
      {children ? (
        <>
          {expanded ? (
            <Box style={style}>
              <CustomListItem
                onClick={handleClick}
                style={style}
              >
                <CustomListItemIcon style={{ color: theme.palette.primary.light }}>
                  {icon && icon}
                </CustomListItemIcon>
                <Typography variant="body2">{text}</Typography>
                <CustomListItemCollapsibleIcon>
                  {open ? (
                    <ExpandLess style={{ color: theme.palette.primary.light }} />
                  ) : (
                    <ExpandMore style={{ color: theme.palette.primary.light }} />
                  )}
                </CustomListItemCollapsibleIcon>
              </CustomListItem>
              <Collapse
                in={open}
                timeout="auto"
                unmountOnExit
              >
                <List
                  component="div"
                  disablePadding
                >
                  {children}
                </List>
              </Collapse>
            </Box>
          ) : (
            <Box style={style}>{children}</Box>
          )}
        </>
      ) : (
        <>
          {expanded ? (
            <CustomNavLink
              target={to.indexOf('https://') === 0 ? '_blank' : '_self'}
              to={to}
              style={style}
              $expanded={expanded}
            >
              <CustomNavLinkIcon
                style={{ color: theme.palette.primary.light }}
                $expanded={expanded}
              >
                {icon && icon}
              </CustomNavLinkIcon>
              <Typography variant="body2">{text}</Typography>
            </CustomNavLink>
          ) : (
            <Tooltip
              placement="right"
              title={tooltip ?? ''}
              arrow
            >
              <ListItem
                sx={{
                  ...style,
                  padding: '0',
                  my: '10px',
                  justifyContent: expanded ? 'flex-start' : 'center',
                }}
              >
                <CustomNavLink
                  to={to}
                  target={to.indexOf('https://') === 0 ? '_blank' : '_self'}
                  $expanded={expanded}
                >
                  <CustomNavLinkIcon
                    style={{ color: theme.palette.primary.light }}
                    $expanded={expanded}
                  >
                    {icon && icon}
                  </CustomNavLinkIcon>
                </CustomNavLink>
              </ListItem>
            </Tooltip>
          )}
        </>
      )}
    </>
  );
};

export default SidebarNavItem;
